import type { IconButtonProps } from '@workramp/ui';
import type { Ref } from 'react';
import { forwardRef } from 'react';
import { useHubTheming } from '../../../providers/useHubTheming';
import { getMutedColor } from '../../utils';
import { StyledIconButton } from './ThemeIconButton.styles';

export interface ThemeIconButtonProps extends IconButtonProps {
  themeColor?: string;
  themeHoverColor?: string;
  themeTextColor?: string;
}

const HUB_GENERAL = 'hub.general';

export const ThemeIconButton = forwardRef(
  (
    { themeColor, themeHoverColor, themeTextColor, color = 'primary', ...props }: ThemeIconButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => {
    const { renderedThemeSettings } = useHubTheming();
    const hubGeneralSettings = renderedThemeSettings.find((ts) => ts.category === HUB_GENERAL).settings;
    const { colors: defaultHubColors } = hubGeneralSettings;

    const HUB_COLORS = {
      text: defaultHubColors.text,
      buttonAndLinks: defaultHubColors.buttonLinks,
      buttonText: '#FFFFFF',
    };

    const hubDefaultColors = {
      primary: {
        color: HUB_COLORS.buttonAndLinks,
        textColor: HUB_COLORS.buttonText,
      },
      secondary: {
        color: getMutedColor(HUB_COLORS.text),
        hoverColor: HUB_COLORS.buttonAndLinks,
      },
    }[color];

    return (
      <BaseThemeIconButton
        ref={ref}
        {...props}
        themeColor={themeColor || hubDefaultColors.color}
        themeHoverColor={themeHoverColor || hubDefaultColors.hoverColor}
        themeTextColor={themeTextColor || hubDefaultColors.textColor}
      />
    );
  },
);

export const BaseThemeIconButton = forwardRef(
  ({ themeColor, themeHoverColor, themeTextColor, ...props }: ThemeIconButtonProps, ref: Ref<HTMLButtonElement>) => (
    <StyledIconButton
      ref={ref}
      {...props}
      $color={themeColor}
      $hoverColor={themeHoverColor}
      $textColor={themeTextColor}
    />
  ),
);
